import { useEffect } from 'react'

/**
 * Generic confirm-action modal. No existing modal component was in this
 * codebase to reuse -- every destructive action so far used the browser's
 * native `confirm()` (see ReportGroupsPage.tsx, ConsolidationPage.tsx,
 * etc.) -- this is the first one, introduced for the microgrid delete/
 * remove actions specifically, which the product owner asked to have a
 * real confirm modal rather than the native dialog.
 */
export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  isDangerous = true,
  isConfirming = false,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  isDangerous?: boolean
  isConfirming?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onCancel}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-message"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-3xl border border-nav-line bg-card p-6 shadow-[0_10px_28px_rgba(16,40,26,0.16),0_1px_3px_rgba(16,40,26,0.08)]"
      >
        <h2 id="confirm-modal-title" className="text-base font-semibold text-text-primary">
          {title}
        </h2>
        <p id="confirm-modal-message" className="mt-2 text-sm text-text-secondary">
          {message}
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isConfirming}
            className="rounded-full border border-nav-line px-5 py-2 text-sm font-semibold text-text-primary hover:bg-nav-hover disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirming}
            autoFocus
            className={`rounded-full px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 ${
              isDangerous ? 'bg-error' : 'bg-brand'
            }`}
          >
            {isConfirming ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
